import { VisualizationModule } from '@core/models/data-visualization/data-visualization.model';

import {
  CalendarEvent,
  CalendarEventModification
} from '@features/components/visualization/calendar/models/calendar.models';
import {
  List,
  ListItem
} from '@features/components/visualization/list/models/list-visualization';
import {
  GaliaArea,
  GeoAreaModification
} from '@features/components/visualization/mapbox/models/galia-area';
import { GaliaMarker } from '@features/components/visualization/mapbox/models/galia-marker';
import { GaliaRoute } from '@features/components/visualization/mapbox/models/galia-route';
import { OccupationSeries } from '@features/components/visualization/occupation/models/occupation.model';
import { TimelineRow } from '@features/components/visualization/timeline/models/timeline';
import { TimelineModificationDescriptors } from '@features/components/visualization/timeline/models/timeline-modification-descriptor';

import { AuxiliaryDataContext } from '../../services/auxiliary-data/auxiliary-data.service';
import {
  EntityCfg,
  EntityInterface,
  EntityModificationAction,
  hasAreaModificationCapability,
  hasCalendarModificationCapability,
  hasCalendarVisuCapability,
  hasListVisuCapability,
  hasMapCapability,
  hasModificationCapability,
  hasOccupationVisuCapability,
  hasTimelineCapability
} from '../entities/entity-visualization-interface';

export interface OpenedVisualizationWindows {
  calendar: boolean;
  list: boolean;
  map: boolean;
  timeline: boolean;
  occupation: boolean;
}

export interface MapVisualizationState {
  markers: GaliaMarker[];
  routes: GaliaRoute[];
  areas: GaliaArea[];
}

export interface TimelineVisualizationState {
  rows: TimelineRow[];
}

export interface ListVisualizationState {
  nbElems: number;
  lists: List[];
}

export interface CalendarVisualizationState {
  events: CalendarEvent[];
}

/**
 * Lives outside `VisuComponentStates` because it is not derived from the selection: a dock is
 * contested between routes, most of which are never selected, so the series are fetched per
 * site instead. Keeping it there would also have every selection change overwrite it, since
 * `setVisuComponentStates` replaces that object wholesale.
 */
export interface OccupationVisualizationState {
  series: OccupationSeries[];
  /** True while sites are being fetched. */
  loading: boolean;
  /** Sites the current selection puts on screen; what the module asks for. */
  siteUidsInView: string[];
  /**
   * Every site asked for so far in this context, never shrinking. The series are rebuilt
   * over the whole set on each request so that the popup asking for one site cannot drop
   * the module's other panels, and so `usagesReported` is decided over everything loaded.
   */
  requestedSiteUids: string[];
}

export enum FilterMethod {
  DISABLED = 'disabled',
  BY_HIGHLIGHT_MARKER = 'map-marker-selected',
  BY_AGGREGATOR = 'aggregator'
}

/** The sentinel aggregator ids `filteredAggregatorIds` uses in place of a real route. */
export enum MapFilterType {
  RESET = '0',
  UNSELECTED_ALL_GROUP_ID = '-1'
}

export interface AggregatorFilterState {
  filteredAggregatorIds: string[];
  filterMethod: FilterMethod;
  markerId?: string | undefined;
  /**
   * The action a job pin was raised from. Only the seed travels: each window walks the
   * job graph against its own index, so the lit set has one definition, not one per window.
   */
  focusedActionUid?: string | undefined;
}

export interface VisuComponentStates {
  availableVisualizations: Set<VisualizationModule>;
  mapVisualization: MapVisualizationState;
  timelineVisualization: TimelineVisualizationState;
  listVisualization: ListVisualizationState;
  calendarVisualization: CalendarVisualizationState;
}

export interface DataVisualizationState {
  openedVisualizationWindows: OpenedVisualizationWindows;
  filterOnAggregator: AggregatorFilterState;
  componentStates: VisuComponentStates;
  occupationVisualization: OccupationVisualizationState;
  /** Dataspace and plan the current selection was resolved against; scopes every fetch. */
  visualizationContext: AuxiliaryDataContext | null;
  isHydrating: boolean;
  timelineModificationDescriptors: TimelineModificationDescriptors;
  timelineTimeStep: number | null;
}

export class MapVisuFactory {
  static buildVisuState(entities: EntityInterface<unknown>[]): {
    mapState: MapVisualizationState;
    modificationsMap: Map<string, GeoAreaModification>;
  } {
    const markers = entities.flatMap((entity) => {
      if (hasMapCapability(entity)) {
        return entity.buildMarkers();
      }
      return [];
    });
    const routes = entities.flatMap((entity) => {
      if (hasMapCapability(entity)) {
        return entity.buildRoutes(markers);
      }
      return [];
    });
    const areas = entities.flatMap((entity) => {
      if (hasMapCapability(entity)) {
        return entity.buildAreas();
      }
      return [];
    });
    const modificationsMap = new Map<string, GeoAreaModification>();
    for (const entity of entities) {
      if (hasAreaModificationCapability(entity)) {
        for (const modification of entity.buildAreaModifications()) {
          modificationsMap.set(modification.uid, modification);
        }
      }
    }

    return {
      mapState: { markers, routes, areas },
      modificationsMap
    };
  }
}

export class TimelineVisuFactory {
  static buildVisuState(entities: EntityInterface<unknown>[]): {
    timelineState: TimelineVisualizationState;
    modificationActionsMap: Map<string, EntityModificationAction[]>;
  } {
    const rows: TimelineRow[] = [];
    const modificationActionsMap = new Map<string, EntityModificationAction[]>();

    for (const entity of entities) {
      if (hasTimelineCapability(entity)) {
        const entityRows = entity.buildTimelineRows();
        rows.push(...entityRows);

        if (hasModificationCapability(entity)) {
          const actions = entity.getModificationActions();
          for (const row of entityRows) {
            modificationActionsMap.set(row.uid, actions);
          }
        }
      }
    }

    return { timelineState: { rows }, modificationActionsMap };
  }
}

export class CalendarVisuFactory {
  static buildVisuState(entities: EntityInterface<unknown>[]): {
    calendarState: CalendarVisualizationState;
    modificationsMap: Map<string, CalendarEventModification>;
  } {
    const events: CalendarEvent[] = [];
    const modificationsMap = new Map<string, CalendarEventModification>();
    for (const entity of entities) {
      if (hasCalendarVisuCapability(entity)) {
        events.push(...entity.buildCalendarEvents());
      }
      if (hasCalendarModificationCapability(entity)) {
        for (const mod of entity.buildCalendarModifications()) {
          modificationsMap.set(mod.uid, mod);
        }
      }
    }
    return { calendarState: { events }, modificationsMap };
  }
}

export class ListVisuFactory {
  static buildVisuState(entities: EntityInterface<unknown>[]): ListVisualizationState {
    const lists = entities.flatMap((entity) => {
      if (hasListVisuCapability(entity)) {
        return entity.buildLists();
      }
      return [];
    });
    let nextElemId = 0;
    lists.forEach((list: List) => {
      list.items.forEach((item: ListItem) => {
        item.appElementId = nextElemId;
        list.appElemIds.push(nextElemId);
        nextElemId += 1;
      });
    });
    return {
      nbElems: nextElemId,
      lists
    };
  }
}

/**
 * The sites the selection puts on screen, deduplicated. A dock is contested between routes,
 * so this is all an entity can say about occupation: which site to go and ask about.
 */
export function collectOccupationSiteUids(entities: EntityInterface<unknown>[]): string[] {
  const uids = new Set<string>();
  for (const entity of entities) {
    if (!hasOccupationVisuCapability(entity)) continue;
    for (const uid of entity.occupationSiteUids()) uids.add(uid);
  }
  return [...uids];
}

export class DataVisuFactory {
  static buildVisuComponentStates(
    entityCfg: EntityCfg<unknown>,
    entities: EntityInterface<unknown>[]
  ): {
    states: VisuComponentStates;
    timelineModificationActionsMap: Map<string, EntityModificationAction[]>;
    calendarModificationsMap: Map<string, CalendarEventModification>;
    areaModificationsMap: Map<string, GeoAreaModification>;
  } {
    const availableVisualizations = entityCfg.supportedVisualizations();
    const timelineResult = TimelineVisuFactory.buildVisuState(entities);
    const calendarResult = CalendarVisuFactory.buildVisuState(entities);
    const mapResult = MapVisuFactory.buildVisuState(entities);

    return {
      states: {
        availableVisualizations,
        mapVisualization: mapResult.mapState,
        timelineVisualization: timelineResult.timelineState,
        listVisualization: ListVisuFactory.buildVisuState(entities),
        calendarVisualization: calendarResult.calendarState
      },
      timelineModificationActionsMap: timelineResult.modificationActionsMap,
      calendarModificationsMap: calendarResult.modificationsMap,
      areaModificationsMap: mapResult.modificationsMap
    };
  }
}
